/**
 * Ask Vera — a docked right panel.
 *
 * The panel/modal distinction is the whole design. This is a flex sibling of the page in
 * Layout, so opening it makes the page NARROWER rather than covering it: no backdrop, no
 * focus trap, no scroll lock. You can read a table, open the panel, ask about what you
 * are looking at, and still click the table. A MUI `Drawer` — the obvious reach, and what
 * Filters.tsx uses — is modal by default and would take all three of those away.
 *
 * The one thing flex layout cannot fix is `position: fixed` chrome, which is measured
 * against the viewport and cannot see that the page shrank. The panel publishes its width
 * as a CSS variable for those; see config/layout.ts `fullBleedBarWidth`.
 *
 * ── OPEN/CLOSE IS A SLIDE, NOT A POP (VRG-227) ──────────────────────────────────────
 * This component is now ALWAYS RENDERED — `open` no longer gates an early `return null`.
 * That is deliberate, not an oversight: a CSS `transition` only animates a property
 * CHANGE on an EXISTING element. A freshly-mounted node has no prior computed style to
 * transition FROM, so the classic "conditionally mount, then toggle a class" approach
 * needs a `requestAnimationFrame` (or two) between the two states to give the browser
 * something to interpolate from — and this app's own preview tooling fires ZERO rAF
 * callbacks (see the `preview-pane-skips-rendering-steps` note), so anything gated on
 * rAF would simply never animate there. Staying always-mounted sidesteps the problem
 * entirely: `open` flipping is a real style change on a node the browser already
 * painted, so the transition fires correctly with no JS scheduling at all.
 *
 * The VISUAL motion is `transform: translateX(...)` — compositor-only, so it never
 * reflows this panel's own rich content (markdown, message bubbles) as it moves, unlike
 * animating `width` would. `width` itself does not use a `transition` — it SNAPS between
 * `0` (closed) and the resolved width (open), because the amount of time a snap takes is
 * zero regardless of curve, so there is nothing for a duration to smooth. The snap is
 * just staged with `transition-delay`, which is where "keep the element mounted through
 * the exit animation" actually happens:
 *   - OPENING: width snaps to the full value with NO delay, in the same instant the
 *     slide-in transform starts — so the panel already has somewhere to slide INTO.
 *   - CLOSING: width (and `visibility`) snap to `0`/`hidden` only AFTER the transform's
 *     own duration has elapsed — so the panel stays fully present, still occupying its
 *     layout space, for the entire time it is visibly sliding away, and only actually
 *     leaves the layout once it can no longer be seen.
 * `main` (Layout.tsx) gets a matching `width` transition of its own (with the SAME
 * duration/curve) so its resize — an instant snap today — settles alongside the slide
 * instead of jumping the moment this panel's reserved space changes. `resizing` (in the
 * ask store) turns that off for the duration of a live drag, for the reason its own
 * docblock gives.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  ChevronRightIcon,
  HistoryIcon,
  MaximizeIcon,
  MinimizeIcon,
  PanelRightCloseIcon,
  PlusIcon,
} from 'lucide-react'

import { Box, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material'

import { ASK_PANEL_WIDTH_VAR } from 'config'
import { zoomOf } from 'utils/textZoom'

import {
  ASK_PANEL_SLIDE_DURATION_MS,
  ASK_PANEL_SLIDE_EASE,
  ASK_WIDTH_MIN,
  clampAskWidth,
  useAskStore,
} from 'ask'

import { AskConversationView } from './AskConversationView'
import { AskHistoryView } from './AskHistoryView'

/** Grab area for the resize handle. Narrower than this and it is hard to hit. */
const HANDLE_WIDTH = 8

/** Keyboard resize step, in px — arrow keys move continuously now, not between stops. */
const KEYBOARD_STEP = 32

export const AskPanel = () => {
  const theme = useTheme()

  const open = useAskStore(s => s.open)
  const width = useAskStore(s => s.width)
  const view = useAskStore(s => s.view)
  const expanded = useAskStore(s => s.expanded)
  const setWidth = useAskStore(s => s.setWidth)
  const setResizing = useAskStore(s => s.setResizing)
  const setView = useAskStore(s => s.setView)
  const closePanel = useAskStore(s => s.closePanel)
  const startConversation = useAskStore(s => s.startConversation)
  const toggleExpanded = useAskStore(s => s.toggleExpanded)

  /**
   * Publish the width for fixed-position chrome.
   *
   * Written to the document element rather than passed as a prop because the readers are
   * save bars on unrelated pages that have no path to this component. Kept at 0px while
   * closed so `calc(100% - 80px - var(--ask-panel-width, 0px))` needs no open/closed
   * branch at the call site.
   *
   * KNOWN GAP: this still publishes the DRAGGED `width` while `expanded` — the panel's
   * actual rendered width in that state is "everything `main` gave up" (see Layout.tsx),
   * not this number. `main` is collapsed to nothing at the same time, so any full-bleed
   * fixed bar reading this var belongs to a page that is not on screen anyway; left
   * unhandled rather than plumbing an "effective width" through for a case with no known
   * visible symptom.
   */
  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty(ASK_PANEL_WIDTH_VAR, open ? `${width}px` : '0px')
    // Braces matter: removeProperty returns the old value, and a cleanup that returns a
    // string is not a valid EffectCallback.
    return () => {
      root.style.removeProperty(ASK_PANEL_WIDTH_VAR)
    }
  }, [open, width])

  // --- resize ---------------------------------------------------------------
  // Continuous drag, driven imperatively off a ref during the move so a fast drag does
  // not re-render (and reflow) the message list on every pointermove — only the panel
  // Box's own inline width is touched while dragging. React — and the persisted store —
  // only learn the final width on pointer-up, which is also the one write that goes
  // through `setWidth` (and therefore localStorage).
  const panelRef = useRef<HTMLDivElement>(null)
  const dragging = useRef(false)
  /**
   * The latest clamped width, mirrored where it can be read synchronously.
   *
   * `endDrag` needs the final width to commit it to the store, and reading it back off
   * the DOM node (`panelRef.current.style.width`) would work but is indirect for no
   * reason — this is simpler and just as synchronous.
   */
  const latestDrag = useRef<number | null>(null)
  // The one bit of drag state that DOES live in React: whether to paint the grip
  // handle in its active colour. Two writes per drag (start, end), not one per
  // pointermove, so this does not reintroduce the jank the ref above avoids.
  const [isDragging, setIsDragging] = useState(false)

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault()
      dragging.current = true
      ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
      // Prevents the drag from selecting the transcript's text as the pointer crosses it.
      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'col-resize'
      setIsDragging(true)
      // Tells `main` (Layout.tsx) to drop its own width transition for the duration of
      // the drag — see `resizing`'s docblock in useAskStore.
      setResizing(true)
    },
    [setResizing],
  )

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return
    // The panel is anchored right, so its width is the distance from the pointer to the
    // right edge of the window — measured in PAINTED px (clientX, innerWidth), while the
    // `style.width` it is written into is LAYOUT px inside `#app`'s text zoom. Dividing
    // by the panel's zoom keeps the grip under the pointer; without it the edge trailed
    // the cursor by (1 − z) of the distance (at 0.8 a 600px drag drew a 480px panel).
    const next = (window.innerWidth - e.clientX) / zoomOf(panelRef.current)
    const clamped = clampAskWidth(next)
    latestDrag.current = clamped
    if (panelRef.current) panelRef.current.style.width = `${clamped}px`
  }, [])

  const endDrag = useCallback(() => {
    if (!dragging.current) return
    dragging.current = false
    document.body.style.userSelect = ''
    document.body.style.cursor = ''
    setIsDragging(false)
    setResizing(false)
    const final = latestDrag.current
    latestDrag.current = null
    // Commits the width the imperative writes above already painted, so there is
    // nothing for the browser to repaint here — this only makes it durable (state +
    // localStorage), not visible.
    if (final !== null) setWidth(final)
    // Clear the raw inline `style.width` `onPointerMove` wrote directly to the DOM node.
    // That write bypasses React/emotion entirely, and a literal inline style attribute
    // always wins the CSS cascade over ANY class-based rule for the same property —
    // including the `sx.width` this component renders on every subsequent state change
    // (open, close, collapse). Left in place, the panel's width gets stuck at whatever
    // it was last dragged to: closing afterward flips `visibility`/`transform` correctly
    // but the box keeps reserving its dragged width in the flex layout (`visibility:
    // hidden` still occupies space), leaving a dead gap on the right the same size as
    // the last drag. Only manifests after at least one drag — a plain open/close with no
    // resize never touches this inline attribute. Clearing it here hands width control
    // back to `sx` with no visual change, since the store now holds the same value.
    if (panelRef.current) panelRef.current.style.width = ''
  }, [setResizing, setWidth])

  const onHandleKeyDown = (e: React.KeyboardEvent) => {
    // Continuous now, like the drag — a fixed px step rather than jumping between
    // stops that no longer exist.
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      setWidth(width + KEYBOARD_STEP)
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault()
      setWidth(width - KEYBOARD_STEP)
    }
  }

  /**
   * `flex-grow`/`flex-basis` are the PRE-EXISTING fullscreen-takeover transition (see
   * the `expanded` branch below) and have to stay in this list — they were the whole
   * `transition` declaration before this change, and dropping them here would silently
   * turn that unrelated feature's animation off.
   *
   * The close direction additionally stages its `width`/`visibility` snap AFTER the
   * slide finishes (see this file's header docblock); the open direction applies them
   * with no delay at all, so the panel already has somewhere to slide into from the
   * first frame. Same two declarations, opposite delay — written out per direction
   * because a single template string with a conditional delay is harder to read than
   * just branching.
   */
  /**
   * `width` animates in BOTH directions now, on the same curve and duration as the slide,
   * so the panel moving and the page resizing are one motion rather than two.
   * `visibility` is the only thing still staged: it may only flip to `hidden` once the
   * panel has finished sliding, or it would disappear mid-travel.
   */
  const slideTransition = open
    ? `flex-grow 220ms ease, flex-basis 220ms ease, transform ${ASK_PANEL_SLIDE_DURATION_MS}ms ${ASK_PANEL_SLIDE_EASE}, width ${ASK_PANEL_SLIDE_DURATION_MS}ms ${ASK_PANEL_SLIDE_EASE}, visibility 0ms linear 0ms`
    : `flex-grow 220ms ease, flex-basis 220ms ease, transform ${ASK_PANEL_SLIDE_DURATION_MS}ms ${ASK_PANEL_SLIDE_EASE}, width ${ASK_PANEL_SLIDE_DURATION_MS}ms ${ASK_PANEL_SLIDE_EASE}, visibility 0ms linear ${ASK_PANEL_SLIDE_DURATION_MS}ms`

  return (
    <Box
      ref={panelRef}
      component="aside"
      aria-label="Ask Vera"
      // Screen readers and keyboard tab order should skip the panel while it is closed
      // (or mid-exit, once the delayed `visibility` snap below has actually landed) —
      // this is the accessible half of a component that is now ALWAYS mounted rather
      // than removed from the tree.
      aria-hidden={!open}
      sx={[
        {
          position: 'relative',
          flexShrink: 0,
          // Matches the main column exactly — which is now `100%` of the app row rather
          // than a viewport expression (VRG-836): the space banner sits ABOVE that row, so
          // a panel measured against the viewport would hang the banner's height below the
          // fold. The text-zoom reasoning that put a `calc(100vh / var(--text-zoom, 1))`
          // here still holds and still applies — it is just stated once, on the column
          // wrapper in Layout, instead of in each of the three columns.
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          /**
           * The CARD surface, not the sidebar's.
           *
           * This has been both, and the reasoning for each is worth keeping.
           *
           * It started on `background.paper` and read as a giant full-height card. Moving
           * it to `sidebarSurface` fixed that by making it chrome — and the argument was
           * sound as far as it went: the panel and the sidebar both flank the content
           * column. But it produced a collision. In LIGHT mode `sidebarSurface` and
           * `fieldFill` are the same `#fafafa`, so the composer's input became exactly its
           * own background and lost all figure/ground; it needed a bespoke override to
           * claw it back.
           *
           * Sharing a surface with the sidebar was the wrong conclusion anyway. The
           * sidebar holds NAVIGATION; this holds a conversation, a scrollable transcript
           * and an input. Functionally it is a content surface that happens to be docked,
           * so the card level is where it belongs — and in dark mode that also places it
           * BELOW the sidebar (`#0f0f12` against `#18181b`) rather than level with it,
           * which is what "the panel should be darker than the sidebar" was asking for.
           *
           * The composer's override goes away with this: at the card level the default
           * `fieldFill` is once again distinct from the surround, in both themes, with no
           * special case.
           */
          bgcolor: 'background.paper',
          borderLeft: `1px solid ${theme.palette.divider}`,
          // The slide (VRG-227). `transform`, not `width` — a translate is
          // compositor-only, so it never reflows this panel's own message list/markdown
          // as it moves. `width` and `visibility` ride along in the SAME `transition`
          // declaration purely to STAGE their snap via `transition-delay` (see
          // `slideTransition` above and this file's header docblock) — a 0ms-duration
          // transition has nothing to interpolate, so it never fights a live drag's
          // direct `style.width` writes below.
          /**
           * A FIXED PIXEL distance, not `translateX(100%)`.
           *
           * The width now animates alongside this (see below), and a percentage translate
           * resolves against the element's CURRENT width — so the two would compound: as
           * the box narrowed, `100%` would shrink with it and the panel would decelerate
           * into the edge instead of clearing it. Pixels make the travel independent of
           * the box.
           */
          transform: open ? 'translateX(0)' : `translateX(${width}px)`,
          visibility: open ? 'visible' : 'hidden',
          transition: slideTransition,
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        },
        expanded
          ? // Fullscreen takeover: grow to fill everything `main` just gave up (see
            // Layout.tsx), rather than computing a pixel width from the sidebar's own
            // size. `width` itself is left untouched in the store, so collapsing
            // falls straight back to it below.
            { flexGrow: 1, flexBasis: 0, width: 'auto' }
          : // Reserved layout width: full when open (or still mid-close-slide — the
            // `transition-delay` above is what keeps this at `width` rather than
            // snapping to 0 immediately), `0` once actually closed. `closePanel` always
            // resets `expanded` to `false` in the same action, so this branch and the
            // slide above are never in conflict.
            /**
             * Animated over the SAME duration as the slide, with no delay — `main`
             * (Layout.tsx) sizes itself from whatever this leaves behind, so this value IS
             * what drives the page reflowing back.
             *
             * It previously snapped AFTER the slide, first via a `transition-delay` and
             * then via a JS timer. Both produced the same defect: measured across a close,
             * the transform animated 0 -> 706px while `main` sat at 1177 for the entire
             * 232ms, then jumped to 1817 in a single frame. The panel left, and THEN the
             * page snapped wider. Sequential motion reads as a flash however smooth each
             * half is alone.
             *
             * The original reason for delaying it was that animating `width` reflows the
             * panel's own rich content as it moves. That cost is real but invisible here —
             * the panel is translating off-screen at the same time, so nobody sees the
             * reflow, whereas everybody saw the page jump.
             */
            { flexGrow: 0, width: open ? width : 0 },
      ]}
    >
      {/* The drag handle only makes sense at a dragged, OPEN width — disabled (and
          hidden) during the fullscreen takeover and while closed, rather than left to
          silently do nothing (or, while closed, sit interactively at the screen's edge
          with nothing behind it). */}
      {open && !expanded && (
        <Box
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize Ask panel"
          aria-valuemin={ASK_WIDTH_MIN}
          aria-valuenow={Math.round(width)}
          tabIndex={0}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={onHandleKeyDown}
          sx={{
            position: 'absolute',
            // Still straddles the border — the hit target stays exactly as generous as
            // before. Only the VISIBLE grip (below) moves inward; the invisible strip
            // people actually have to hit is unchanged.
            left: -HANDLE_WIDTH / 2,
            top: 0,
            bottom: 0,
            width: HANDLE_WIDTH,
            cursor: 'col-resize',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // Touch: without this a drag on a touchscreen also tries to scroll/zoom the
            // page underneath the handle.
            touchAction: 'none',
            '&:hover .ask-panel-grip, &:focus-visible .ask-panel-grip': {
              backgroundColor: theme.palette.brand[600],
              opacity: 1,
            },
          }}
        >
          {/*
            A VISIBLE grip, not an invisible hit area — the strip above is the (generous)
            hit target, this pill is what says "drag me". Quiet at rest (divider, 60%
            opacity), full brand colour on hover/focus/drag so the affordance confirms
            itself the moment you touch it. `pointerEvents: none` keeps it out of the
            hit-test so `e.target` in the handlers above is always the strip, never this.

            Nudged 2px off the strip's centre, TOWARD the panel — it used to sit dead
            centre on the border itself (half in the page, half in the panel); now it
            floats just inside, reading as the panel's own affordance rather than
            straddling the seam between the two.
          */}
          <Box
            className="ask-panel-grip"
            sx={{
              width: 3,
              height: 32,
              borderRadius: 999,
              pointerEvents: 'none',
              backgroundColor: isDragging ? theme.palette.brand[600] : theme.palette.divider,
              opacity: isDragging ? 1 : 0.6,
              transform: 'translateX(2px)',
              transition: 'background-color 120ms ease, opacity 120ms ease',
            }}
          />
        </Box>
      )}

      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          flexShrink: 0,
          px: 4,
          py: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/*
          Parity with the rest of the chrome (VRG-227 polish pass). This was `body2`
          (16px) with `size="small"` 18px icons — measured against the two closest
          "docked panel with a title + close icon" precedents in this codebase, the
          shared `Modal` header (`packages/ui/components/feedback/Modal`, title
          `body1`/20px, close icon at lucide's bare default of 24px, no `size="small"`)
          and the `Filters` drawer (`components/input/filters/Filters.tsx`, title
          `variant="body1"`, close `<XIcon strokeWidth={1}/>` with neither prop set) —
          both land on the same pair of values, so this is "what the app already uses"
          rather than a new size. `fontWeight: 600` is kept (both precedents are 400)
          because this title is closer to a page identity than a dialog caption.
        */}
        <Typography variant="body1" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
          Ask Vera
        </Typography>

        {/* "New chat", not "New question" — this holds multi-turn conversations with
            history, so "question" undersold it. Plain `+`, not the busier
            message-with-plus glyph: at this size the tooltip carries the meaning, and
            the extra bubble detail only made it harder to tell apart from its
            neighbours (§21). */}
        <Tooltip title="New chat">
          <IconButton onClick={() => startConversation()} aria-label="New chat">
            <PlusIcon strokeWidth={1} />
          </IconButton>
        </Tooltip>

        {/* The view flip. History is a toggle rather than a separate route because the
            panel is a companion to whatever page you are on — navigating away from that
            page to read your own past questions would defeat the point of docking it. */}
        <Tooltip title={view === 'history' ? 'Back to conversation' : 'History'}>
          <IconButton
            onClick={() => setView(view === 'history' ? 'conversation' : 'history')}
            aria-label={view === 'history' ? 'Back to conversation' : 'History'}
            aria-pressed={view === 'history'}
            sx={{ color: view === 'history' ? theme.palette.brand[600] : undefined }}
          >
            {view === 'history' ? <ChevronRightIcon strokeWidth={1} /> : <HistoryIcon strokeWidth={1} />}
          </IconButton>
        </Tooltip>

        {/* Fullscreen takeover (VRG-227) — grouped with Close as window chrome, right
            before it, rather than beside the content actions above. */}
        <Tooltip title={expanded ? 'Collapse panel' : 'Expand panel'}>
          <IconButton
            onClick={toggleExpanded}
            aria-label={expanded ? 'Collapse Ask panel' : 'Expand Ask panel'}
            aria-pressed={expanded}
          >
            {expanded ? <MinimizeIcon strokeWidth={1} /> : <MaximizeIcon strokeWidth={1} />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Close panel">
          <IconButton onClick={closePanel} aria-label="Close Ask panel">
            <PanelRightCloseIcon strokeWidth={1} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {view === 'history' ? <AskHistoryView /> : <AskConversationView />}
      </Box>
    </Box>
  )
}
